import {
  HHButton,
  HHDivider,
  HHLoadingButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useRef } from 'react';
import { downloadCsvTemplate, parseProgramCapCsv } from './csv-utils';

interface BatchImportToolbarProps {
  rowCount: number;
  hasRows: boolean;
  loading: boolean;
  submitEnabled: boolean;
  onRowsImported: (
    rows: Awaited<ReturnType<typeof parseProgramCapCsv>>,
  ) => void;
  onSubmit: () => void;
}

export const BatchImportToolbar = ({
  rowCount,
  hasRows,
  loading,
  submitEnabled,
  onRowsImported,
  onSubmit,
}: BatchImportToolbarProps): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await parseProgramCapCsv(file);
    onRowsImported(result);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <HHStack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        hidden
        onChange={handleFileUpload}
        data-testid="csv-file-input"
      />
      <HHButton
        hhVariant="variant-bypass"
        variant="outlined"
        startIcon={<UploadFileIcon />}
        onClick={(): void => fileInputRef.current?.click()}
      >
        Import CSV
      </HHButton>
      <HHButton
        hhVariant="variant-bypass"
        variant="text"
        startIcon={<DownloadIcon />}
        onClick={downloadCsvTemplate}
      >
        Download Template
      </HHButton>
      {hasRows && (
        <HHTypography hhVariant="body2" color="text.secondary">
          {rowCount} row{rowCount !== 1 ? 's' : ''}
        </HHTypography>
      )}
      <HHDivider orientation="vertical" />
      <HHLoadingButton
        hhVariant="variant-bypass"
        variant="contained"
        disabled={!submitEnabled}
        loading={loading}
        onClick={onSubmit}
      >
        Create Program Caps
      </HHLoadingButton>
    </HHStack>
  );
};
